import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import ApiClient from '../../util/api-client';
import type {
  PurchaseOrder,
  PurchaseOrderCreateInput,
  PurchaseOrderDeleteInput,
  PurchaseOrderItem,
} from '@repo/api-contract';
import dayjs from 'dayjs';

export enum PurchaseOrderModalMode {
  CREATE = 'create',
  VIEW = 'view',
  EDIT = 'edit',
}

export interface PurchaseOrderModalState {
  stateIsChanged: boolean;
  isOpenPurchaseOrderModal: boolean;
  purchaseOrderIdToEdit?: string;
  deletePurchaseOrderModal: boolean;
  purchaseOrderToDelete: PurchaseOrder | undefined;
  mode: PurchaseOrderModalMode;
  loading: boolean;
  disableButtons: boolean;
  updateCallback?: (
    newPurchaseOrder: PurchaseOrder | undefined,
  ) => Promise<void>;
  createCallback?: (
    newPurchaseOrder: PurchaseOrder | undefined,
  ) => Promise<void>;
  deleteCallback?: (
    newPurchaseOrder: PurchaseOrder | undefined,
  ) => Promise<void>;
  formData: PurchaseOrderCreateInput;
}

const initialFormData: PurchaseOrderCreateInput = {
  orderId: '',
  code: '',
  rate: 0,
  orderDate: 0,
  estimatedShipDate: 0,
  customerDeposit: 0,
  shipMethod: '',
  deadline: 0,
  items: [],
  inHandsDate: 0,
  vendorName: '',
  vendorAddress: '',
  vendorAccountNumber: '',
  shipToName: '',
  shipToAddress: '',
  notes: '',
  paymentTerms: [],
};

// Initialize state
const initialState: PurchaseOrderModalState = {
  stateIsChanged: false,
  isOpenPurchaseOrderModal: false,
  purchaseOrderIdToEdit: undefined,
  deletePurchaseOrderModal: false,
  purchaseOrderToDelete: undefined,
  mode: PurchaseOrderModalMode.CREATE,
  loading: false,
  disableButtons: false,
  formData: initialFormData,
};

export const purchaseOrderModalSlice = createSlice({
  name: 'purchaseOrderModal',
  initialState,
  reducers: {
    openPurchaseOrderModal: (
      state,
      action: PayloadAction<
        | {
            createCallback?: (
              newPurchaseOrder: PurchaseOrder | undefined,
            ) => Promise<void>;
            resetState?: boolean;
            formData?: PurchaseOrderCreateInput;
          }
        | undefined
      >,
    ) => {
      // If resetState is true, reset the formData and any other relevant fields
      if (action.payload?.resetState) {
        return {
          ...initialState,
          isOpenPurchaseOrderModal: true,
          createCallback: action.payload.createCallback,
          formData: action.payload.formData
            ? {
                ...action.payload.formData,
                orderDate:
                  action.payload.formData?.orderDate ??
                  dayjs().startOf('day').unix(),
              }
            : {
                ...initialFormData,
                orderDate: dayjs().startOf('day').unix(),
              }, // Reset form data back to initial
        };
      }

      // Otherwise, open the modal without resetting
      return {
        ...state,
        isOpenPurchaseOrderModal: true,
        createCallback: action.payload?.createCallback,
      };
    },
    openEditPurchaseOrderModal: (
      state,
      action: PayloadAction<{
        purchaseOrderIdToEdit: string;
        mode?: PurchaseOrderModalMode;
        updateCallback?: (user: PurchaseOrder | undefined) => Promise<void>;
      }>,
    ) => {
      return {
        ...initialState,
        purchaseOrderIdToEdit: action.payload.purchaseOrderIdToEdit,
        isOpenPurchaseOrderModal: true,
        updateCallback: action.payload?.updateCallback,
        isEditingPurchaseOrder: true,
        mode: action.payload?.mode || PurchaseOrderModalMode.EDIT,
      };
    },
    setFormData: (state, action: PayloadAction<PurchaseOrderCreateInput>) => {
      // Update form data in redux whenever form changes
      return {
        ...state,
        formData: action.payload,
        stateIsChanged: true,
      };
    },
    addItemsToPurchaseOrder: (
      state,
      action: PayloadAction<PurchaseOrderItem[]>,
    ) => {
      return {
        ...state,
        formData: {
          ...state.formData,
          items: [...state.formData.items, ...action.payload],
        },
        stateIsChanged: true,
      };
    },
    setMode: (state, action: PayloadAction<PurchaseOrderModalMode>) => ({
      ...state,
      mode: action.payload,
    }),
    closeModal: (
      state,
      action: PayloadAction<{ resetState?: boolean } | undefined>,
    ) => {
      if (action.payload?.resetState) return { ...initialState };
      return {
        ...state,
        isOpenPurchaseOrderModal: false,
        deletePurchaseOrderModal: false,
      };
    },
    openDeletePurchaseOrderModal: (
      state,
      action: PayloadAction<{
        purchaseOrderToDelete: PurchaseOrder;
        deleteCallback?: (user: PurchaseOrder | undefined) => Promise<void>;
      }>,
    ) => {
      return {
        ...state,
        deletePurchaseOrderModal: true,
        purchaseOrderToDelete: action.payload.purchaseOrderToDelete,
        deleteCallback: action.payload.deleteCallback,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDisableButtons: (state, action: PayloadAction<boolean>) => {
      state.disableButtons = action.payload;
    },
  },
});

export const {
  openPurchaseOrderModal,
  openEditPurchaseOrderModal,
  addItemsToPurchaseOrder,
  closeModal,
  openDeletePurchaseOrderModal,
  setMode,
  setLoading,
  setDisableButtons,
  setFormData,
} = purchaseOrderModalSlice.actions;

// Thunks
export const fetchPurchaseOrderToEdit =
  (id: string) =>
  async (dispatch: Dispatch): Promise<PurchaseOrder> => {
    const purchaseOrder: PurchaseOrder =
      await ApiClient.purchaseOrderGetById.query(id);
    return purchaseOrder;
  };

export const deletePurchaseOrder =
  (deleteInput: PurchaseOrderDeleteInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setLoading(true));
      await ApiClient.deletePurchaseOrder.mutate(deleteInput);
    } finally {
      dispatch(setLoading(false));
    }
  };

export default purchaseOrderModalSlice.reducer;
